/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback, useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { getSoilSightScenario } from "services";

import { productAnalytics, useCreateSoilSightScenario, useDeleteSoilSightScenario, useReplaceSoilSightCropYears, useTranslation } from "utils";

import { EditableFieldKey } from "consts";

import { SoilSightScenarioDetail } from "models";

import type { PostHydrationLatch } from "pages/app/scenario-analysis/hooks/usePostHydrationLatch";
import { type CropYear, type ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { restoreEditableFields } from "pages/app/scenario-analysis/utils/detailedCalculationsEdit";
import { watchProjectSwitch } from "pages/app/scenario-analysis/utils/projectSwitch";
import {
  type AutosaveRefs,
  isApiScenarioId,
  restoreFailedDeletions,
  showSoilSightSaveFailed,
  syncScenarioDetailCache,
  toCropYearsPayload,
} from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { fromApiScenario } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import type { AutosavedChangeType } from "pages/app/scenario-analysis/utils/soilSightTracking";

type ScenarioEditingArgs = {
  scenarios: ScenarioData[];
  setScenarios: Dispatch<SetStateAction<ScenarioData[]>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  isDirtyRef: MutableRefObject<boolean>;
  /** Dirty state per scenario before its first Detailed calculations edit, so Discard can put it back. */
  dirtyBeforeTabEditsRef: MutableRefObject<Map<string, boolean>>;
  autosaveRefs: AutosaveRefs;
  projectEpochRef: MutableRefObject<number>;
  postHydrationLatch: PostHydrationLatch;
  ensureProjectId: () => Promise<string | null>;
  setAwaitingInitialCalc: Dispatch<SetStateAction<boolean>>;
  setDroppedCropsBannerDismissed: Dispatch<SetStateAction<boolean>>;
  scrollToScenarioCard: (scenarioId: string) => void;
  pruneAndPersistEmptyYears: (currentScenarios: ScenarioData[]) => Promise<ScenarioData[] | null>;
  passesValidation: (scenarios: ScenarioData[]) => boolean;
  restoreChangeDots: () => void;
  detailedCalcsIsEditing: boolean;
  markScenarioChange: (scenarioId: string, changeType: AutosavedChangeType) => void;
};

/** Every way the user changes scenarios on the page: the builder (add, delete, rename, rotation drawer) and the Detailed calculations tab. */
export const useScenarioEditing = ({
  scenarios,
  setScenarios,
  setIsDirty,
  isDirtyRef,
  dirtyBeforeTabEditsRef,
  autosaveRefs,
  projectEpochRef,
  postHydrationLatch,
  ensureProjectId,
  setAwaitingInitialCalc,
  setDroppedCropsBannerDismissed,
  scrollToScenarioCard,
  pruneAndPersistEmptyYears,
  passesValidation,
  restoreChangeDots,
  detailedCalcsIsEditing,
  markScenarioChange,
}: ScenarioEditingArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { cropYearsSavedRef, scenarioMetaSavedRef } = autosaveRefs;
  const queryClient = useQueryClient();
  const { mutateAsync: createScenarioAsync } = useCreateSoilSightScenario();
  const { mutateAsync: deleteScenarioAsync } = useDeleteSoilSightScenario();
  const { mutateAsync: replaceCropYearsAsync } = useReplaceSoilSightCropYears();

  const handleScenariosChange = useCallback(
    async function handleScenariosChange(next: ScenarioData[]) {
      // Additions go through handleCreateScenario, so this only persists deletions.
      const nextById = new Map(next.map((scenario) => [scenario.id, scenario]));
      const removed = scenarios.filter((scenario) => !nextById.has(scenario.id));

      setScenarios(next);
      setIsDirty(true);
      // User started fixing the rotation → the unsupported-crops banner has served its purpose.
      setDroppedCropsBannerDismissed(true);

      // Keeps the detail cache in sync, so a remount hydrates from memory instead of refetching.
      next.forEach((scenario) => {
        if (!isApiScenarioId(scenario.id)) return;
        syncScenarioDetailCache(queryClient, scenario);
      });

      const deletable = removed.filter((gone) => isApiScenarioId(gone.id));
      if (deletable.length === 0) return;
      const isSameProject = watchProjectSwitch(projectEpochRef);
      const results = await Promise.allSettled(deletable.map((gone) => deleteScenarioAsync(gone.id)));
      // Restoring into a project switched to meanwhile would show (and autosave) the previous project's scenarios.
      if (!isSameProject()) return;
      const failedIds = new Set<string>();
      results.forEach((result, index) => {
        const gone = deletable[index];
        if (result.status === "fulfilled") {
          cropYearsSavedRef.current.delete(gone.id);
          scenarioMetaSavedRef.current.delete(gone.id);
        } else {
          failedIds.add(gone.id);
        }
      });
      // We put failed deletes back below, so they still count.
      results
        .filter((result) => result.status === "fulfilled")
        .forEach(() => productAnalytics.track("soilsights:scenario_delete", { scenario_count: next.length + failedIds.size }));
      if (failedIds.size === 0) return;
      // The server still has these scenarios, so put them back instead of leaving the page out of sync.
      setScenarios((current) => restoreFailedDeletions(scenarios, current, failedIds));
      const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      showSoilSightSaveFailed(t, firstFailure?.reason);
    },
    [
      cropYearsSavedRef,
      deleteScenarioAsync,
      projectEpochRef,
      queryClient,
      scenarioMetaSavedRef,
      scenarios,
      setDroppedCropsBannerDismissed,
      setIsDirty,
      setScenarios,
      t,
    ]
  );

  const handleCreateScenario = useCallback(
    async function handleCreateScenario(title: string, templateScenarioId?: string): Promise<void> {
      // The new scenario is incomplete on purpose, so the post-hydration run is skipped and its loader cleared here.
      postHydrationLatch.markDecided();
      setAwaitingInitialCalc(false);
      const baseline = scenarios.find((scenario) => scenario.isReference);
      const isFromBaseline = !!templateScenarioId && baseline?.id === templateScenarioId;
      const isSameProject = watchProjectSwitch(projectEpochRef);

      try {
        const id = await ensureProjectId();
        const created = await createScenarioAsync({
          soilSightProjectId: id ?? "",
          body: { name: title, duplicateFromBaseline: isFromBaseline },
        });
        const createdScenarioId = created?.scenarioId ?? null;

        if (!createdScenarioId) {
          showSoilSightSaveFailed(t);
          return;
        }

        // The create response has only the id, so the detail is fetched; a non-baseline copy then replaces the crop years (best effort).
        let cropYears: SoilSightScenarioDetail["cropYears"] = [];
        let scenarioName = title;
        let scenarioIsReference = false;
        let scenarioCreatedAt: string | undefined;
        try {
          const detail = await getSoilSightScenario(createdScenarioId);
          if (detail) {
            scenarioName = detail.name ?? scenarioName;
            scenarioIsReference = detail.isReference ?? scenarioIsReference;
            scenarioCreatedAt = detail.createdAt ?? scenarioCreatedAt;
            cropYears = Array.isArray(detail.cropYears) ? detail.cropYears : cropYears;
          }
        } catch {
          // Ignore detail hydration failure and continue with the data we already have.
        }
        if (templateScenarioId) {
          const source = scenarios.find((scenario) => scenario.id === templateScenarioId);
          if (source) {
            const body = toCropYearsPayload(source.years);
            try {
              await replaceCropYearsAsync({ scenarioId: createdScenarioId, body });
              cropYears = body.cropYears;
            } catch (copyError) {
              // The scenario exists on the server, so it is still added with the crop years it was created with.
              showSoilSightSaveFailed(t, copyError);
            }
          }
        }
        if (!isSameProject()) return;

        const newScenario = fromApiScenario({
          id: createdScenarioId,
          name: scenarioName,
          isReference: scenarioIsReference,
          createdAt: scenarioCreatedAt,
          cropYears,
        });
        setScenarios((curr) => [...curr, newScenario]);
        productAnalytics.track("soilsights:scenario_create", { creation_mode: templateScenarioId ? "copy" : "fresh", scenario_count: scenarios.length + 1 });
        scrollToScenarioCard(createdScenarioId);

        // Prime the saved refs so the background autosave doesn't immediately re-PUT what we just persisted.
        cropYearsSavedRef.current.set(createdScenarioId, JSON.stringify({ cropYears }));
        scenarioMetaSavedRef.current.set(createdScenarioId, JSON.stringify({ name: scenarioName }));
        // New scenarios are not reflected in the latest outcomes until the next recalc.
        setIsDirty(true);
      } catch (saveError) {
        showSoilSightSaveFailed(t, saveError);
      }
    },
    [
      createScenarioAsync,
      cropYearsSavedRef,
      ensureProjectId,
      postHydrationLatch,
      projectEpochRef,
      replaceCropYearsAsync,
      scenarioMetaSavedRef,
      scenarios,
      scrollToScenarioCard,
      setAwaitingInitialCalc,
      setIsDirty,
      setScenarios,
      t,
    ]
  );

  const handlePersistScenarioYears = useCallback(
    async function handlePersistScenarioYears(scenarioId: string, years: ScenarioData["years"]): Promise<boolean> {
      if (!isApiScenarioId(scenarioId)) return false;
      const body = toCropYearsPayload(years);
      const isSameProject = watchProjectSwitch(projectEpochRef);
      try {
        await replaceCropYearsAsync({ scenarioId, body });
        if (!isSameProject()) return true;
        cropYearsSavedRef.current.set(scenarioId, JSON.stringify(body));
        // Built from the saved years: this closure's scenarios may predate the drawer's setScenarios.
        const postSaveScenarios = scenarios.map((scenario) => (scenario.id === scenarioId ? { ...scenario, years } : scenario));
        const pruned = await pruneAndPersistEmptyYears(postSaveScenarios);
        if (!pruned) {
          // The drawer's save landed, so the outcomes are stale even though the prune failed (toast shown).
          if (isSameProject()) setIsDirty(true);
          return true;
        }
        // Saving marks the outcomes stale; the next run comes from the settings bar or a tab arrival.
        setIsDirty(true);
        passesValidation(pruned);
        return true;
      } catch (saveError) {
        showSoilSightSaveFailed(t, saveError);
        return false;
      }
    },
    [cropYearsSavedRef, passesValidation, projectEpochRef, pruneAndPersistEmptyYears, replaceCropYearsAsync, scenarios, setIsDirty, t]
  );

  const handleEditCropYearField = useCallback(
    function handleEditCropYearField(scenarioId: string, yearId: string, field: EditableFieldKey, value: number | null) {
      if (!dirtyBeforeTabEditsRef.current.has(scenarioId)) dirtyBeforeTabEditsRef.current.set(scenarioId, isDirtyRef.current);
      markScenarioChange(scenarioId, "economics");
      const savedAt = Date.now();
      setScenarios((current) =>
        current.map((scenario) =>
          scenario.id === scenarioId
            ? { ...scenario, years: scenario.years.map((year) => (year.id === yearId ? { ...year, [field]: value, updatedAt: savedAt } : year)) }
            : scenario
        )
      );
      setIsDirty(true);
    },
    [dirtyBeforeTabEditsRef, isDirtyRef, markScenarioChange, setIsDirty, setScenarios]
  );

  // Discarding in the tab: its writes already landed, so the pre-edit values and timestamps are written back.
  const handleDiscardCropYearEdits = useCallback(
    function handleDiscardCropYearEdits(scenarioId: string, snapshot: CropYear[]): boolean {
      const target = scenarios.find((scenario) => scenario.id === scenarioId);
      if (!target) return false;
      const restoredYears = restoreEditableFields(target.years, snapshot);
      const wasDirtyBeforeEdits = dirtyBeforeTabEditsRef.current.get(scenarioId) ?? true;
      dirtyBeforeTabEditsRef.current.delete(scenarioId);

      setScenarios((current) => current.map((scenario) => (scenario.id === scenarioId ? { ...scenario, years: restoredYears } : scenario)));
      if (wasDirtyBeforeEdits || !isApiScenarioId(scenarioId)) {
        setIsDirty(true);
        return false;
      }

      // Outcomes match the restored inputs again, so no rerun; autosave skips a clean page, so the restore is saved here.
      setIsDirty(false);
      restoreChangeDots();
      const body = toCropYearsPayload(restoredYears);
      const serialized = JSON.stringify(body);
      const isSameProject = watchProjectSwitch(projectEpochRef);
      // Sent even when the ledger already matches: an edit's save may still be in flight, and this one must land after it.
      replaceCropYearsAsync({ scenarioId, body })
        .then(() => {
          if (isSameProject()) cropYearsSavedRef.current.set(scenarioId, serialized);
        })
        .catch((saveError) => {
          // Dirty again, so the next run (or a project switch) flushes the restore that didn't save.
          if (isSameProject()) setIsDirty(true);
          showSoilSightSaveFailed(t, saveError);
        });
      return true;
    },
    [cropYearsSavedRef, dirtyBeforeTabEditsRef, projectEpochRef, replaceCropYearsAsync, restoreChangeDots, scenarios, setIsDirty, setScenarios, t]
  );

  useEffect(
    function resetTabEditDirtyOnEditToggle() {
      dirtyBeforeTabEditsRef.current.clear();
    },
    [detailedCalcsIsEditing, dirtyBeforeTabEditsRef]
  );

  return { handleScenariosChange, handleCreateScenario, handlePersistScenarioYears, handleEditCropYearField, handleDiscardCropYearEdits };
};
